import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Alert, AlertService, ModalComponent, ModalResult, TableConfig } from '@michelin/theme';
import { CompanyFormComponent } from 'src/app/shared/components/forms/company-form/company-form.component';
import { AccountManagerRequestDto } from 'src/app/shared/models/distributor/account-manager-dto';
import { DistributorDto, DistributorNoIdDto } from 'src/app/shared/models/distributor/distributor-dto';
import { StateDto } from 'src/app/shared/models/location/state-dto';
import { ProductLineDto } from 'src/app/shared/models/product-line/product-line-dto';
import { UserDto } from 'src/app/shared/models/user/user-dto';
import { AccountManagerService } from 'src/app/shared/services/distributor/account-manager.service';

export declare class AccountManager {
  id: number;
  name: string;
  email: string;
}

@Component({
    selector: 'app-distributor-form',
    templateUrl: './distributor-form.component.html',
    standalone: false
})
export class DistributorFormComponent implements OnInit {
  loading = false;

  form: FormGroup;
  userForm: FormGroup;
  distributorId: number;

  @Input() readonly: boolean = false;
  @Input() states: StateDto[] = [];
  @Input() users: UserDto[] = [];
  @Input() productLines: ProductLineDto[] = [];
  @Input() accountManagers: AccountManager[] = [];

  @Output() accountManagerUpdate = new EventEmitter();

  @ViewChild(CompanyFormComponent) companyForm: CompanyFormComponent;
  @ViewChild('userModal') userModal: ModalComponent;
  @ViewChild('userModalContent') userModalContent: TemplateRef<any>;

  tableConfig: TableConfig<AccountManager> = {
    columns: {
      name: { header: 'Nome', cell: (item) => item.name },
      email: { header: 'Email', cell: (item) => item.email },
      actions: { header: '  ', cell: 'actionsCell', width: '80px', align: 'center' }
    }
  };

  constructor(
    private readonly builder: FormBuilder,
    private readonly alertService: AlertService,
    private readonly accountManagerService: AccountManagerService
  ) {
    this.form = this.builder.group({
      code: ['', Validators.required],
      user: [''],
      productLine: ['', Validators.required],
      active: [true, Validators.required]
      // deleted: [false, Validators.required]
      /** O componente <app-company-form/> vai adicionar o formGroup company */
    });
    this.userForm = this.builder.group({
      user: ['', Validators.required]
    });
  }

  get productLine(): FormControl {
    return this.form.get('productLine') as FormControl;
  }

  get user(): FormControl {
    return this.form.get('user') as FormControl;
  }

  get accountManager(): FormControl {
    return this.userForm.get('user') as FormControl;
  }

  get formValues(): DistributorNoIdDto {
    return {
      code: this.form.value.code,
      status: this.form.value.active ? 'A' : 'I',
      deleted: this.form.value.deleted,
      productLineId: this.form.value.productLine?.id,
      userId: this.form.value.user?.id,
      company: this.companyForm?.formValues
    };
  }

  get accountManagerOptions(): UserDto[] {
    return this.users.filter((user) => user.userProfile?.userType === 'MICHELIN');
  }

  get accountManagerDataSource(): MatTableDataSource<AccountManager> {
    return new MatTableDataSource(this.accountManagers);
  }

  loadFormValues(data: DistributorDto) {
    this.distributorId = data.id;
    this.form.patchValue({
      code: data.code,
      active: data.status == 'A' ? true : false,
      deleted: data.deleted,
      user: data.user,
      productLine: data.productLine
    });
    this.companyForm.loadFormValues(data.company);
  }

  codeDisplayName(data: any) {
    return data?.code || '';
  }

  userDisplayName(user: UserDto): string {
    return user ? `${user.name} (${user.userId})` : '';
  }

  onAddAMClick() {
    this.userForm.reset();
    this.userModal.open({
      id: 'userModalContent',
      width: 480,
      title: 'Adicionar Account Manager',
      content: this.userModalContent,
      confirm: 'Confirmar',
      cancel: 'Cancelar'
    });
  }

  onUserModalClosed(event: ModalResult) {
    if (event.result) {
      if (this.userForm.valid) {
        this.loading = true;
        const data: AccountManagerRequestDto = {
          distributorId: this.distributorId,
          userId: this.userForm.value.user.id
        };
        this.accountManagerService
          .create(data)
          .subscribe({
            next: () => this.alertService.showAlert({ type: 'success', content: 'AM adicionado com sucesso' } as Alert),
            error: (error: any) => {
              const message = error?.error?.message || '';
              this.alertService.showAlert({ type: 'critical', content: 'Ocorreu um erro ao adicionar o AM. ' + message } as Alert);
            }
          })
          .add(() => {
            this.loading = false;
            this.accountManagerUpdate.emit();
          });
      } else {
        this.alertService.showAlert({ type: 'strong', title: 'Selecione um Distribuidor para realizar a alteração' } as Alert);
      }
    }
  }

  removeAccountManager(accoumtManagerId: number) {
    this.loading = true;
    this.accountManagerService
      .delete(accoumtManagerId)
      .subscribe({
        next: () => this.alertService.showAlert({ type: 'success', content: 'AM removido com sucesso' } as Alert),
        error: (error: any) =>
          this.alertService.showAlert({
            type: 'critical',
            content: 'Ocorreu um erro ao remover o AM. ' + (error?.error?.message || '')
          } as Alert)
      })
      .add(() => {
        this.loading = false;
        this.accountManagerUpdate.emit();
      });
  }

  ngOnInit() {
    if (this.readonly) {
      this.form.disable();
    }
  }
}
